import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";
import {
  type SqlAction,
  type SqlState,
  activeTask,
  decodeSqlOutput,
  encodeSqlOutput,
  readSqlState,
  setupSqlFor,
} from "../../../lib/board/sqlLogic";
import { SQL_DATASETS, datasetInfo } from "../../../lib/board/sqlDatabases";
import { submitSqlTaskRequest } from "../../../lib/board/sqlTaskRequest";
import { tasksForDataset } from "../../../lib/board/sqlTasks";
import {
  type SqlSchemaTable,
  type SqlTableResult,
  compareResults,
  runSql,
  solutionOrdersRows,
} from "../../../lib/board/sqlRunner";
import { mountCodeEditor, getCodeEditor } from "../codeEditorView";
import {
  applyRemoteToEditor,
  applyRemoteToTextarea,
  createLiveTextSender,
} from "../liveTextSync";
import { burstConfetti } from "../confetti";
import { renderSqlErd, SQL_ERD_CSS } from "../sqlErd";


export type SqlRenderOpts = {
  canEdit?: boolean;
  onSqlAction?: (objectId: string, action: SqlAction) => void;
  onRequestSelect?: (objectId: string) => void;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.setAttribute("class", className);
  return node;
}

function stopProp(node: Element) {
  for (const type of ["pointerdown", "mousedown", "click", "dblclick"] as const) {
    node.addEventListener(type, (ev) => ev.stopPropagation());
  }
}

/** id виджетов, в чьём редакторе сейчас печатают. */
const sqlTypingIds = new Set<string>();
/** Последний показанный статус — чтобы конфетти сыпалось один раз на верный ответ. */
const lastStatusById = new Map<string, string>();
/** Схема базы приходит из Worker-а; кэш, чтобы панель не ждала первого запуска. */
const schemaCache = new Map<string, SqlSchemaTable[]>();

export function isSqlWidgetTyping(objectId?: string): boolean {
  if (objectId) return sqlTypingIds.has(objectId);
  return sqlTypingIds.size > 0;
}

export function clearSqlWidgetTyping(objectId?: string): void {
  if (objectId) sqlTypingIds.delete(objectId);
  else sqlTypingIds.clear();
}

function schemaKey(state: SqlState): string {
  return state.dataset === "custom" ? "custom:" + state.setupSql : state.dataset;
}

/** Структурная подпись: пока она совпадает, DOM переиспользуется без пересборки. */
function domSignature(state: SqlState): string {
  return [state.dataset, state.mode, state.showSchema ? "1" : "0"].join("|");
}

export function sqlDomMatchesObject(node: HTMLElement, obj: SceneObject): boolean {
  const card = node.querySelector(".wb-sql-card") as HTMLElement | null;
  if (!card) return false;
  const state = readSqlState(obj as Parameters<typeof readSqlState>[0]);
  return card.dataset.signature === domSignature(state);
}

export function syncSqlObjectLayout(node: HTMLElement, obj: SceneObject): void {
  const w = Math.max(420, Number(obj.w) || 760);
  const h = Math.max(320, Number(obj.h) || 560);
  placeHtmlObject(node, obj, w, h);
}

const FO_CSS = `
.wb-sql-card{box-sizing:border-box;position:relative;width:100%;height:100%;display:flex;flex-direction:column;gap:6px;padding:8px;border-radius:12px;background:linear-gradient(165deg,#0f172a 0%,#1e293b 100%);border:1px solid #334155;box-shadow:0 8px 24px rgba(15,23,42,.28);color:#e2e8f0;font-family:"Segoe UI",system-ui,sans-serif;overflow:hidden;cursor:default}
.wb-sql-card,.wb-sql-card *{cursor:default}
.wb-sql-card__head{display:flex;align-items:center;gap:6px;flex-shrink:0;flex-wrap:wrap}
.wb-sql-card__title{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#94a3b8;margin-right:2px}
.wb-sql-card__select{appearance:none;border:1px solid #475569;background:#1e293b;color:#e2e8f0;border-radius:7px;font-size:11px;font-weight:600;padding:3px 6px;cursor:pointer;max-width:190px}
.wb-sql-card__select:disabled{opacity:.6}
.wb-sql-card__btn{appearance:none;border:1px solid #475569;background:#1e293b;color:#e2e8f0;border-radius:7px;font-size:11px;font-weight:600;padding:3px 8px;cursor:pointer}
.wb-sql-card__btn:hover{background:#334155}
.wb-sql-card__btn:disabled{opacity:.5;cursor:default}
.wb-sql-card__btn.is-on{background:#134e4a;border-color:#0f766e;color:#ccfbf1}
.wb-sql-card__spacer{flex:1 1 auto}
.wb-sql-card__main{flex:1 1 auto;min-height:0;display:flex;gap:8px}
.wb-sql-card__aside{flex:0 0 180px;min-height:0;overflow:auto;overscroll-behavior:contain;border:1px solid #334155;border-radius:8px;background:#020617;padding:6px 8px}
.wb-sql-card__aside-title{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px}
.wb-sql-table{margin-bottom:8px}
.wb-sql-table__name{font-size:12px;font-weight:700;color:#5eead4}
.wb-sql-table__rows{font-size:10px;color:#64748b;margin-left:4px;font-weight:600}
.wb-sql-table__col{font-size:11px;color:#cbd5e1;padding-left:8px;line-height:1.45;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.wb-sql-table__type{color:#64748b;font-size:10px}
.wb-sql-table__pk{color:#fbbf24}
.wb-sql-card__col{flex:1 1 auto;min-width:0;min-height:0;display:flex;flex-direction:column;gap:6px}
.wb-sql-task{flex:0 0 auto;border:1px solid #334155;border-radius:8px;background:rgba(2,6,23,.6);padding:6px 8px;display:flex;flex-direction:column;gap:4px}
.wb-sql-task__nav{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.wb-sql-task__topic{font-size:10px;font-weight:700;color:#5eead4;text-transform:uppercase;letter-spacing:.04em}
.wb-sql-task__done{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#a7f3d0;background:rgba(16,185,129,.16);border:1px solid #10b981;border-radius:999px;padding:2px 8px}
.wb-sql-task__done[hidden]{display:none}
.wb-sql-task__text{font-size:13px;line-height:1.45;color:#e2e8f0;margin:0}
.wb-sql-task__hint{font-size:11px;color:#fcd34d;margin:0;line-height:1.4}
.wb-sql-task__solution{font-size:11px;color:#94a3b8;font-family:ui-monospace,Consolas,monospace;margin:0;white-space:pre-wrap;word-break:break-word}
.wb-sql-card__editor{flex:1 1 auto;min-height:80px;display:flex;flex-direction:column;gap:3px}
.wb-sql-card__label{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em}
.wb-sql-card__host{flex:1 1 auto;min-height:70px;width:100%;overflow:hidden;border-radius:8px;cursor:text}
.wb-sql-card__host,.wb-sql-card__host *{cursor:text}
.wb-sql-card__host .cm-editor{height:100%;min-height:70px}
.wb-sql-card__host .cm-scroller{overscroll-behavior:contain}
.wb-sql-card__setup{flex:0 0 auto;display:flex;flex-direction:column;gap:3px}
.wb-sql-card__setup .wb-sql-card__host{min-height:110px;height:130px;flex:0 0 130px}
.wb-sql-card__foot{display:flex;align-items:center;gap:8px;flex-shrink:0;flex-wrap:wrap}
.wb-sql-card__run{appearance:none;border:none;border-radius:8px;background:#0d9488;color:#ecfdf5;font-size:12px;font-weight:700;padding:6px 12px;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.wb-sql-card__run:hover{background:#0f766e}
.wb-sql-card__run:disabled{opacity:.65;cursor:wait}
.wb-sql-card__check{appearance:none;border:none;border-radius:8px;background:#4f46e5;color:#eef2ff;font-size:12px;font-weight:700;padding:6px 12px;cursor:pointer}
.wb-sql-card__check:hover{background:#4338ca}
.wb-sql-card__check:disabled{opacity:.65;cursor:wait}
.wb-sql-card__status{font-size:11px;font-weight:600;color:#5eead4;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.wb-sql-ask{flex:0 0 auto;display:flex;flex-direction:column;gap:6px;border:1px solid #334155;border-radius:8px;background:rgba(2,6,23,.6);padding:8px}
.wb-sql-ask[hidden]{display:none}
.wb-sql-ask__input{box-sizing:border-box;width:100%;resize:vertical;border-radius:8px;border:1px solid #334155;background:#020617;color:#e2e8f0;font-family:inherit;font-size:12px;line-height:1.4;padding:6px 8px;outline:none;cursor:text}
.wb-sql-ask__input:focus{border-color:#14b8a6}
.wb-sql-ask__row{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.wb-sql-ask__note{font-size:11px;font-weight:600;color:#94a3b8;min-width:0;overflow:hidden;text-overflow:ellipsis}
.wb-sql-ask__note.is-ok{color:#5eead4}
.wb-sql-ask__note.is-error{color:#fda4af}
.wb-sql-card__verdict{flex:0 0 auto;display:none;align-items:center;gap:8px;border-radius:8px;padding:6px 10px;font-size:12px;font-weight:700;line-height:1.35}
.wb-sql-card__verdict.is-ok{display:flex;background:rgba(16,185,129,.16);border:1px solid #10b981;color:#a7f3d0}
.wb-sql-card__verdict.is-fail{display:flex;background:rgba(244,63,94,.14);border:1px solid #f43f5e;color:#fecdd3}
.wb-sql-card__out{flex:0 0 auto;box-sizing:border-box;width:100%;height:150px;min-height:60px;max-height:340px;overflow:auto;overscroll-behavior:contain;resize:vertical;border-radius:8px;border:1px solid #334155;background:#020617;padding:0;cursor:text}
.wb-sql-card__grid{border-collapse:collapse;width:100%;font-family:ui-monospace,Consolas,monospace;font-size:11px}
.wb-sql-card__grid th{position:sticky;top:0;background:#0f172a;color:#5eead4;text-align:left;font-weight:700;padding:5px 8px;border-bottom:1px solid #334155;white-space:nowrap}
.wb-sql-card__grid td{padding:4px 8px;border-bottom:1px solid #1e293b;color:#e2e8f0;white-space:nowrap}
.wb-sql-card__grid tr:nth-child(even) td{background:rgba(148,163,184,.05)}
.wb-sql-card__null{color:#64748b;font-style:italic}
.wb-sql-card__msg{padding:8px 10px;font-size:12px;color:#94a3b8;font-family:ui-monospace,Consolas,monospace;white-space:pre-wrap;word-break:break-word;margin:0}
.wb-sql-card__msg.is-error{color:#fda4af}
.wb-sql-card__confetti{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:5}
.wb-sql-table__name{cursor:pointer}
.wb-sql-table__name:hover{text-decoration:underline}
${SQL_ERD_CSS}
.wb-sql-card,.wb-sql-card *{scrollbar-width:thin;scrollbar-color:#475569 #0f172a}
.wb-sql-card ::-webkit-scrollbar{width:8px;height:8px}
.wb-sql-card ::-webkit-scrollbar-track{background:#0f172a;border-radius:4px}
.wb-sql-card ::-webkit-scrollbar-thumb{background:#475569;border-radius:4px;border:2px solid #0f172a}
.wb-sql-card ::-webkit-scrollbar-thumb:hover{background:#64748b}
`;

function renderSchemaPanel(
  aside: HTMLElement,
  tables: SqlSchemaTable[] | undefined,
  onPickTable?: (name: string) => void
): void {
  aside.textContent = "";
  const title = el("div", "wb-sql-card__aside-title");
  title.textContent = "Схема базы";
  aside.appendChild(title);
  if (!tables || !tables.length) {
    const msg = el("div", "wb-sql-table__col");
    msg.textContent = "Загрузка…";
    aside.appendChild(msg);
    return;
  }
  for (const t of tables) {
    const box = el("div", "wb-sql-table");
    const name = el("div", "wb-sql-table__name");
    name.textContent = t.name;
    name.title = `Показать содержимое таблицы ${t.name}`;
    name.addEventListener("click", (ev) => {
      ev.stopPropagation();
      onPickTable?.(t.name);
    });
    const rows = el("span", "wb-sql-table__rows");
    rows.textContent = `· ${t.rows}`;
    name.appendChild(rows);
    box.appendChild(name);
    for (const c of t.columns) {
      const col = el("div", "wb-sql-table__col");
      col.textContent = c.name;
      col.title = `${c.name} ${c.type}`;
      if (c.pk) {
        const pk = el("span", "wb-sql-table__pk");
        pk.textContent = " 🔑";
        col.appendChild(pk);
      }
      const type = el("span", "wb-sql-table__type");
      type.textContent = c.type ? " " + c.type.toLowerCase() : "";
      col.appendChild(type);
      box.appendChild(col);
    }
    aside.appendChild(box);
  }
}

function renderResult(out: HTMLElement, table: SqlTableResult | null): void {
  out.textContent = "";
  if (!table) {
    const msg = el("p", "wb-sql-card__msg");
    msg.textContent = "Нажмите «Выполнить», чтобы увидеть результат запроса.";
    out.appendChild(msg);
    return;
  }
  if (table.error) {
    const msg = el("p", "wb-sql-card__msg is-error");
    msg.textContent = table.error;
    out.appendChild(msg);
    return;
  }
  const cols = table.columns ?? [];
  const rows = table.rows ?? [];
  if (!cols.length) {
    const msg = el("p", "wb-sql-card__msg");
    msg.textContent = table.note || "Запрос выполнен.";
    out.appendChild(msg);
    return;
  }
  const grid = el("table", "wb-sql-card__grid");
  const thead = el("thead");
  const hr = el("tr");
  for (const c of cols) {
    const th = el("th");
    th.textContent = c;
    hr.appendChild(th);
  }
  thead.appendChild(hr);
  grid.appendChild(thead);
  const tbody = el("tbody");
  for (const row of rows) {
    const tr = el("tr");
    for (const cell of row) {
      const td = el("td");
      if (cell === null || cell === undefined) {
        const nul = el("span", "wb-sql-card__null");
        nul.textContent = "NULL";
        td.appendChild(nul);
      } else {
        td.textContent = String(cell);
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  grid.appendChild(tbody);
  out.appendChild(grid);

  const total = table.total ?? rows.length;
  const foot = el("p", "wb-sql-card__msg");
  foot.textContent = table.truncated
    ? `Показаны первые ${rows.length} строк из ${total}.`
    : `Строк: ${total}.`;
  out.appendChild(foot);
}

function applyVerdict(
  card: HTMLElement,
  bar: HTMLElement,
  status: string,
  message: string,
  objectId: string
): void {
  bar.classList.remove("is-ok", "is-fail");
  if (status === "ok") {
    bar.classList.add("is-ok");
    bar.textContent = "🎉 " + (message || "Верно!");
  } else if (status === "fail") {
    bar.classList.add("is-fail");
    bar.textContent = "✗ " + (message || "Неверно");
  } else {
    bar.textContent = "";
  }
  const prev = lastStatusById.get(objectId);
  if (status === "ok" && prev !== "ok") {
    const canvas = card.querySelector(".wb-sql-card__confetti") as HTMLCanvasElement | null;
    if (canvas) burstConfetti(canvas);
  }
  lastStatusById.set(objectId, status);
}

/**
 * Отметки «сделано»: бейдж у текущего задания, кнопки сброса и галочки в списке.
 * Живут в объекте сцены, поэтому видны обоим участникам.
 */
function updateSolvedUi(card: HTMLElement, state: SqlState): void {
  const task = activeTask(state);
  const solved = Boolean(task && state.solvedIds.includes(task.id));

  const badge = card.querySelector("[data-sql-done]") as HTMLElement | null;
  if (badge) badge.hidden = !solved;
  const redo = card.querySelector("[data-sql-redo]") as HTMLElement | null;
  if (redo) redo.hidden = !solved;
  const reset = card.querySelector("[data-sql-reset]") as HTMLElement | null;
  if (reset) reset.hidden = state.solvedIds.length === 0;

  const picker = card.querySelector("[data-sql-counter]") as HTMLSelectElement | null;
  if (picker) {
    const tasks = tasksForDataset(state.dataset);
    for (let i = 0; i < picker.options.length && i < tasks.length; i++) {
      const mark = state.solvedIds.includes(tasks[i].id) ? "✓ " : "";
      const text = `${mark}${i + 1}/${tasks.length} · ${tasks[i].topic}`;
      if (picker.options[i].textContent !== text) picker.options[i].textContent = text;
    }
  }
}

/** Обновление уже построенной карточки: текст запроса, результат, вердикт, задание. */
/**
 * Подсказка и решение показываются по нажатию, а текст им ставился только в
 * этот момент. При переходе к другому заданию карточка не пересобирается —
 * и на экране оставались подсказка с решением от прошлой задачи. Теперь они
 * закрываются вместе со сменой задания, а если открыты — показывают своё.
 */
function syncTaskHelp(card: HTMLElement, task: { id: string; hint?: string; solution: string } | null): void {
  const hintEl = card.querySelector(".wb-sql-task__hint") as HTMLElement | null;
  const solutionEl = card.querySelector(".wb-sql-task__solution") as HTMLElement | null;
  const hintBtn = card.querySelector("[data-sql-hint-btn]") as HTMLElement | null;
  const taskId = task ? task.id : "";

  card.dataset.sqlHint = task?.hint ?? "";
  card.dataset.sqlSolution = task?.solution ?? "";

  if (card.dataset.sqlHelpTaskId !== taskId) {
    card.dataset.sqlHelpTaskId = taskId;
    if (hintEl) {
      hintEl.hidden = true;
      hintEl.textContent = "";
    }
    if (solutionEl) {
      solutionEl.hidden = true;
      solutionEl.textContent = "";
    }
  } else {
    if (hintEl && !hintEl.hidden) hintEl.textContent = task?.hint ?? "";
    if (solutionEl && !solutionEl.hidden) solutionEl.textContent = task?.solution ?? "";
  }
  // У части заданий подсказки нет — кнопке тогда делать нечего.
  if (hintBtn) hintBtn.hidden = !task?.hint;
}

export function syncSqlObjectOnGroup(node: HTMLElement, obj: SceneObject): boolean {
  const card = node.querySelector(".wb-sql-card") as HTMLElement | null;
  if (!card) return false;
  const state = readSqlState(obj as Parameters<typeof readSqlState>[0]);
  if (card.dataset.signature !== domSignature(state)) return false;
  syncSqlObjectLayout(node, obj);

  // Чужой текст — прямо в открытые поля, даже если в них стоит курсор
  // (свой неотправленный набор при этом не затирается, см. liveTextSync).
  const host = card.querySelector('[data-sql-field="query"]') as HTMLElement | null;
  if (host) applyRemoteToEditor(host, state.query);
  const setupHost = card.querySelector('[data-sql-field="setup"]') as HTMLElement | null;
  if (setupHost) applyRemoteToEditor(setupHost, state.setupSql);
  const promptField = card.querySelector(
    'textarea[data-sql-field="customPrompt"]'
  ) as HTMLTextAreaElement | null;
  if (promptField) applyRemoteToTextarea(promptField, state.customPrompt);
  const solutionField = card.querySelector(
    'textarea[data-sql-field="customSolution"]'
  ) as HTMLTextAreaElement | null;
  if (solutionField) applyRemoteToTextarea(solutionField, state.customSolution);

  const task = activeTask(state);
  const promptEl = card.querySelector(".wb-sql-task__text") as HTMLElement | null;
  if (promptEl && task) promptEl.textContent = task.prompt;
  const topicEl = card.querySelector(".wb-sql-task__topic") as HTMLElement | null;
  if (topicEl && task) topicEl.textContent = task.topic;
  syncTaskHelp(card, task);
  const counter = card.querySelector("[data-sql-counter]") as HTMLSelectElement | null;
  if (counter) counter.value = String(state.taskIndex);

  updateSolvedUi(card, state);

  const out = card.querySelector(".wb-sql-card__out") as HTMLElement | null;
  const bar = card.querySelector(".wb-sql-card__verdict") as HTMLElement | null;
  const payload = decodeSqlOutput(state.output);
  if (out) renderResult(out, payload?.table ?? null);
  if (bar) {
    applyVerdict(card, bar, state.status, payload?.verdict?.message ?? "", obj.id);
  }
  return true;
}

/**
 * Виджет «SQL»: база SQLite в браузере, задания и проверка ответа.
 * Запрос и результат хранятся в объекте сцены, поэтому оба участника видят одно и то же.
 */
export function renderSqlObject(obj: SceneObject, opts: SqlRenderOpts = {}): HTMLElement {
  const g = document.createElement("div");
  g.classList.add("wb-scene-obj", "wb-scene-obj--sql");
  g.dataset.objectId = obj.id;

  const w = Math.max(420, Number(obj.w) || 760);
  const h = Math.max(320, Number(obj.h) || 560);
  placeHtmlObject(g, obj, w, h);

  const state = readSqlState(obj as Parameters<typeof readSqlState>[0]);
  const canEdit = Boolean(opts.canEdit);
  const act = (action: SqlAction) => opts.onSqlAction?.(obj.id, action);

  const card = el("div", "wb-sql-card");
  card.style.width = "100%";
  card.style.height = "100%";
  card.style.boxSizing = "border-box";
  card.dataset.signature = domSignature(state);
  card.setAttribute("data-object-id", obj.id);
  stopProp(card);

  const style = el("style");
  style.textContent = FO_CSS;
  card.appendChild(style);

  const confetti = el("canvas", "wb-sql-card__confetti");
  card.appendChild(confetti);

  /* ── шапка: база, режим, схема ── */
  const head = el("div", "wb-sql-card__head");
  const title = el("span", "wb-sql-card__title");
  title.textContent = "SQL";
  head.appendChild(title);

  const dbSelect = el("select", "wb-sql-card__select");
  dbSelect.title = "База данных";
  dbSelect.disabled = !canEdit;
  for (const ds of SQL_DATASETS) {
    const o = el("option");
    o.value = ds.id;
    o.textContent = ds.title;
    o.title = ds.description;
    dbSelect.appendChild(o);
  }
  dbSelect.value = state.dataset;
  dbSelect.addEventListener("change", () => {
    if (!canEdit) return;
    clearSqlWidgetTyping(obj.id);
    act({ type: "setDataset", dataset: dbSelect.value as SqlState["dataset"] });
  });
  head.appendChild(dbSelect);

  const modeSelect = el("select", "wb-sql-card__select");
  modeSelect.title = "Режим работы";
  modeSelect.disabled = !canEdit;
  const modes: { id: SqlState["mode"]; label: string }[] = [
    { id: "tasks", label: "Задания" },
    { id: "custom", label: "Своя задача" },
    { id: "free", label: "Свободный запрос" },
  ];
  for (const m of modes) {
    if (m.id === "tasks" && state.dataset === "custom") continue;
    const o = el("option");
    o.value = m.id;
    o.textContent = m.label;
    modeSelect.appendChild(o);
  }
  modeSelect.value = state.mode;
  modeSelect.addEventListener("change", () => {
    if (!canEdit) return;
    clearSqlWidgetTyping(obj.id);
    act({ type: "setMode", mode: modeSelect.value as SqlState["mode"] });
  });
  head.appendChild(modeSelect);

  const schemaBtn = el("button", "wb-sql-card__btn" + (state.showSchema ? " is-on" : ""));
  schemaBtn.type = "button";
  schemaBtn.textContent = "Схема";
  schemaBtn.title = "Показать/скрыть список таблиц";
  schemaBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (!canEdit) return;
    clearSqlWidgetTyping(obj.id);
    act({ type: "toggleSchema" });
  });
  head.appendChild(schemaBtn);

  const erdBtn = el("button", "wb-sql-card__btn");
  erdBtn.type = "button";
  erdBtn.textContent = "Диаграмма";
  erdBtn.title = "Схема базы целиком: таблицы и связи между ними";
  head.appendChild(erdBtn);

  const spacer = el("span", "wb-sql-card__spacer");
  head.appendChild(spacer);

  if (opts.onRequestSelect) {
    const moveBtn = el("button", "wb-sql-card__btn wb-sql-card__edit");
    moveBtn.type = "button";
    moveBtn.textContent = "Передвинуть";
    moveBtn.title = "Выделить для перемещения и изменения размера";
    moveBtn.setAttribute("data-object-id", obj.id);
    const selectSelf = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      opts.onRequestSelect?.(obj.id);
    };
    moveBtn.addEventListener("pointerdown", selectSelf);
    moveBtn.addEventListener("click", selectSelf);
    head.appendChild(moveBtn);
  }
  card.appendChild(head);

  /* ── тело: схема слева, работа справа ── */
  const main = el("div", "wb-sql-card__main");
  let aside: HTMLElement | null = null;
  if (state.showSchema) {
    aside = el("aside", "wb-sql-card__aside");
    renderSchemaPanel(aside, schemaCache.get(schemaKey(state)), showTableData);
    main.appendChild(aside);
  }

  const col = el("div", "wb-sql-card__col");
  const task = activeTask(state);
  const tasks = tasksForDataset(state.dataset);

  if (task) {
    const taskBox = el("div", "wb-sql-task");
    const nav = el("div", "wb-sql-task__nav");
    const topic = el("span", "wb-sql-task__topic");
    topic.textContent = task.topic;
    nav.appendChild(topic);

    if (state.mode === "tasks" && tasks.length) {
      const prev = el("button", "wb-sql-card__btn");
      prev.type = "button";
      prev.textContent = "◀";
      prev.title = "Предыдущее задание";
      prev.disabled = !canEdit || state.taskIndex <= 0;
      prev.addEventListener("click", (ev) => {
        ev.stopPropagation();
        clearSqlWidgetTyping(obj.id);
        act({ type: "setTaskIndex", taskIndex: state.taskIndex - 1 });
      });

      const picker = el("select", "wb-sql-card__select");
      picker.setAttribute("data-sql-counter", "1");
      picker.disabled = !canEdit;
      tasks.forEach((t, i) => {
        const o = el("option");
        o.value = String(i);
        const mark = state.solvedIds.includes(t.id) ? "✓ " : "";
        o.textContent = `${mark}${i + 1}/${tasks.length} · ${t.topic}`;
        picker.appendChild(o);
      });
      picker.value = String(Math.min(state.taskIndex, tasks.length - 1));
      picker.addEventListener("change", () => {
        if (!canEdit) return;
        clearSqlWidgetTyping(obj.id);
        act({ type: "setTaskIndex", taskIndex: Number(picker.value) || 0 });
      });

      const next = el("button", "wb-sql-card__btn");
      next.type = "button";
      next.textContent = "▶";
      next.title = "Следующее задание";
      next.disabled = !canEdit || state.taskIndex >= tasks.length - 1;
      next.addEventListener("click", (ev) => {
        ev.stopPropagation();
        clearSqlWidgetTyping(obj.id);
        act({ type: "setTaskIndex", taskIndex: state.taskIndex + 1 });
      });

      nav.appendChild(prev);
      nav.appendChild(picker);
      nav.appendChild(next);
    }

    const doneBadge = el("span", "wb-sql-task__done");
    doneBadge.setAttribute("data-sql-done", "1");
    doneBadge.textContent = "✓ Сделано";
    doneBadge.hidden = true;
    nav.appendChild(doneBadge);

    const spacer2 = el("span", "wb-sql-card__spacer");
    nav.appendChild(spacer2);

    const redoBtn = el("button", "wb-sql-card__btn");
    redoBtn.setAttribute("data-sql-redo", "1");
    redoBtn.type = "button";
    redoBtn.textContent = "Заново";
    redoBtn.title = "Снять отметку «сделано» с этого задания";
    redoBtn.hidden = true;
    redoBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (!canEdit) return;
      act({ type: "unsolve", taskId: task.id });
    });
    nav.appendChild(redoBtn);

    const resetBtn = el("button", "wb-sql-card__btn");
    resetBtn.setAttribute("data-sql-reset", "1");
    resetBtn.type = "button";
    resetBtn.textContent = "Сбросить всё";
    resetBtn.title = "Снять отметки «сделано» со всех заданий — например, перед другим учеником";
    resetBtn.hidden = true;
    resetBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      if (!canEdit) return;
      act({ type: "resetSolved" });
    });
    nav.appendChild(resetBtn);

    const hintText = el("p", "wb-sql-task__hint");
    hintText.hidden = true;
    if (task.hint) {
      const hintBtn = el("button", "wb-sql-card__btn");
      hintBtn.setAttribute("data-sql-hint-btn", "1");
      hintBtn.type = "button";
      hintBtn.textContent = "Подсказка";
      hintBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        hintText.hidden = !hintText.hidden;
        hintText.textContent = card.dataset.sqlHint ?? "";
      });
      nav.appendChild(hintBtn);
    }

    const solutionText = el("pre", "wb-sql-task__solution");
    solutionText.hidden = true;
    const solutionBtn = el("button", "wb-sql-card__btn");
    solutionBtn.setAttribute("data-sql-solution-btn", "1");
    solutionBtn.type = "button";
    solutionBtn.textContent = "Решение";
    solutionBtn.title = "Показать эталонный запрос";
    solutionBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      solutionText.hidden = !solutionText.hidden;
      solutionText.textContent = card.dataset.sqlSolution ?? "";
    });
    nav.appendChild(solutionBtn);

    taskBox.appendChild(nav);

    if (state.mode === "custom" && canEdit) {
      const promptInput = el("textarea", "wb-sql-card__msg");
      promptInput.value = state.customPrompt;
      promptInput.rows = 2;
      promptInput.placeholder = "Условие задачи";
      promptInput.style.cssText =
        "width:100%;box-sizing:border-box;background:#020617;border:1px solid #334155;border-radius:8px;color:#e2e8f0;font-family:inherit;font-size:12px;padding:6px 8px;resize:vertical;cursor:text";
      promptInput.dataset.sqlField = "customPrompt";
      const promptSender = createLiveTextSender(promptInput, {
        initial: state.customPrompt,
        enabled: canEdit,
        read: () => promptInput.value,
        send: (v) => act({ type: "setCustomPrompt", customPrompt: v }),
      });
      promptInput.addEventListener("input", () => promptSender.schedule());
      promptInput.addEventListener("focus", () => sqlTypingIds.add(obj.id));
      promptInput.addEventListener("blur", () => {
        sqlTypingIds.delete(obj.id);
        promptSender.flush();
      });
      taskBox.appendChild(promptInput);

      const solLabel = el("div", "wb-sql-card__label");
      solLabel.textContent = "Эталонный запрос (по нему идёт проверка)";
      taskBox.appendChild(solLabel);

      const solInput = el("textarea", "wb-sql-card__msg");
      solInput.value = state.customSolution;
      solInput.rows = 2;
      solInput.placeholder = "SELECT …";
      solInput.style.cssText =
        "width:100%;box-sizing:border-box;background:#020617;border:1px solid #334155;border-radius:8px;color:#a7f3d0;font-family:ui-monospace,Consolas,monospace;font-size:12px;padding:6px 8px;resize:vertical;cursor:text";
      solInput.dataset.sqlField = "customSolution";
      const solSender = createLiveTextSender(solInput, {
        initial: state.customSolution,
        enabled: canEdit,
        read: () => solInput.value,
        send: (v) => act({ type: "setCustomSolution", customSolution: v }),
      });
      solInput.addEventListener("input", () => solSender.schedule());
      solInput.addEventListener("focus", () => sqlTypingIds.add(obj.id));
      solInput.addEventListener("blur", () => {
        sqlTypingIds.delete(obj.id);
        solSender.flush();
      });
      taskBox.appendChild(solInput);
    } else {
      const text = el("p", "wb-sql-task__text");
      text.textContent = task.prompt;
      taskBox.appendChild(text);
    }

    taskBox.appendChild(hintText);
    taskBox.appendChild(solutionText);
    card.dataset.sqlHelpTaskId = task.id;
    card.dataset.sqlHint = task.hint ?? "";
    card.dataset.sqlSolution = task.solution;
    col.appendChild(taskBox);
  }

  /* ── скрипт своей базы ── */
  if (state.dataset === "custom") {
    const setupWrap = el("div", "wb-sql-card__setup");
    const setupLab = el("div", "wb-sql-card__label");
    setupLab.textContent = "Скрипт базы (CREATE TABLE, INSERT)";
    const setupHost = el("div", "wb-sql-card__host");
    setupHost.dataset.sqlField = "setup";
    setupHost.addEventListener("pointerdown", (ev) => ev.stopPropagation());
    const setupSender = createLiveTextSender(setupHost, {
      initial: state.setupSql,
      enabled: canEdit,
      read: () => getCodeEditor(setupHost)?.getValue() ?? "",
      send: (v) => act({ type: "setSetupSql", setupSql: v }),
    });
    mountCodeEditor(setupHost, {
      language: "sql",
      value: state.setupSql,
      readOnly: !canEdit,
      minHeight: "110px",
      onInput: () => setupSender.schedule(),
      onFocus: () => sqlTypingIds.add(obj.id),
      onBlur: () => {
        sqlTypingIds.delete(obj.id);
        setupSender.flush();
      },
    });
    setupWrap.appendChild(setupLab);
    setupWrap.appendChild(setupHost);
    col.appendChild(setupWrap);
  }

  /* ── редактор запроса ── */
  const editorWrap = el("div", "wb-sql-card__editor");
  const editorLab = el("div", "wb-sql-card__label");
  editorLab.textContent = "Ваш запрос";
  const host = el("div", "wb-sql-card__host");
  host.dataset.sqlField = "query";
  host.addEventListener("pointerdown", (ev) => ev.stopPropagation());

  const querySender = createLiveTextSender(host, {
    initial: state.query,
    enabled: canEdit,
    read: () => getCodeEditor(host)?.getValue() ?? "",
    send: (v) => act({ type: "setQuery", query: v }),
  });
  const flushQuery = () => querySender.flush();

  mountCodeEditor(host, {
    language: "sql",
    value: state.query,
    readOnly: !canEdit,
    minHeight: "70px",
    onInput: () => {
      if (!canEdit) return;
      sqlTypingIds.add(obj.id);
      querySender.schedule();
    },
    onFocus: () => sqlTypingIds.add(obj.id),
    onBlur: () => {
      flushQuery();
      window.setTimeout(() => {
        if (!host.contains(document.activeElement)) sqlTypingIds.delete(obj.id);
      }, 0);
    },
  });
  editorWrap.appendChild(editorLab);
  editorWrap.appendChild(host);
  col.appendChild(editorWrap);

  /* ── кнопки и результат ── */
  const foot = el("div", "wb-sql-card__foot");
  const runBtn = el("button", "wb-sql-card__run");
  runBtn.type = "button";
  runBtn.textContent = "▶ Выполнить";
  const checkBtn = el("button", "wb-sql-card__check");
  checkBtn.type = "button";
  checkBtn.textContent = "✓ Проверить";
  checkBtn.title = "Сравнить результат с ответом задания";
  const statusEl = el("span", "wb-sql-card__status");
  foot.appendChild(runBtn);
  if (task) foot.appendChild(checkBtn);
  foot.appendChild(statusEl);

  /* ── просьба о новых заданиях: уходит в админку ── */
  const askForm = el("div", "wb-sql-ask");
  askForm.hidden = true;
  const askInput = el("textarea", "wb-sql-ask__input");
  askInput.rows = 2;
  askInput.placeholder =
    "Какие задания нужны? Например: побольше на JOIN трёх таблиц и на подзапросы.";
  askInput.addEventListener("focus", () => sqlTypingIds.add(obj.id));
  askInput.addEventListener("blur", () => sqlTypingIds.delete(obj.id));
  const askRow = el("div", "wb-sql-ask__row");
  const askSend = el("button", "wb-sql-card__check");
  askSend.type = "button";
  askSend.textContent = "Отправить";
  const askCancel = el("button", "wb-sql-card__btn");
  askCancel.type = "button";
  askCancel.textContent = "Отмена";
  const askNote = el("span", "wb-sql-ask__note");
  askRow.appendChild(askSend);
  askRow.appendChild(askCancel);
  askRow.appendChild(askNote);
  askForm.appendChild(askInput);
  askForm.appendChild(askRow);

  if (canEdit) {
    const askBtn = el("button", "wb-sql-card__btn");
    askBtn.type = "button";
    askBtn.textContent = "Ещё задания";
    askBtn.title = "Попросить добавить задания — запрос придёт преподавателю в админку";
    askBtn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      askForm.hidden = !askForm.hidden;
      askNote.textContent = "";
      askNote.className = "wb-sql-ask__note";
      if (!askForm.hidden) askInput.focus();
    });
    foot.appendChild(askBtn);

    askCancel.addEventListener("click", (ev) => {
      ev.stopPropagation();
      askForm.hidden = true;
    });

    askSend.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      const text = askInput.value.trim();
      if (text.length < 3) {
        askNote.textContent = "Напишите пару слов, каких заданий не хватает.";
        askNote.className = "wb-sql-ask__note is-error";
        return;
      }
      askSend.disabled = true;
      askNote.className = "wb-sql-ask__note";
      askNote.textContent = "Отправляю…";
      try {
        const tasks = tasksForDataset(state.dataset);
        await submitSqlTaskRequest({
          message: text,
          dataset: state.dataset,
          datasetTitle: datasetInfo(state.dataset).title,
          taskNumber: state.mode === "tasks" ? state.taskIndex + 1 : undefined,
          solvedCount: state.solvedIds.length,
          topics: Array.from(new Set(tasks.map((t) => t.topic))).slice(0, 20),
        });
        askInput.value = "";
        askNote.textContent = "Отправлено — запрос придёт преподавателю.";
        askNote.className = "wb-sql-ask__note is-ok";
        window.setTimeout(() => {
          askForm.hidden = true;
        }, 2_000);
      } catch (e) {
        askNote.textContent = e instanceof Error ? e.message : "Не удалось отправить.";
        askNote.className = "wb-sql-ask__note is-error";
      } finally {
        askSend.disabled = false;
      }
    });
  }
  col.appendChild(foot);

  col.appendChild(askForm);

  const verdict = el("div", "wb-sql-card__verdict");
  col.appendChild(verdict);

  const out = el("div", "wb-sql-card__out");
  const initial = decodeSqlOutput(state.output);
  renderResult(out, initial?.table ?? null);
  col.appendChild(out);

  main.appendChild(col);
  card.appendChild(main);

  /* ── диаграмма схемы (поверх карточки, у каждого своя) ── */
  const erd = el("div", "wb-sql-erd");
  erd.hidden = true;
  const erdBar = el("div", "wb-sql-erd__bar");
  const erdTitle = el("span", "wb-sql-erd__title");
  erdTitle.textContent = "Схема базы";
  const erdHint = el("span", "wb-sql-erd__hint");
  erdHint.textContent = "Нажмите на таблицу — покажу её содержимое";
  const erdSpacer = el("span", "wb-sql-erd__spacer");
  const erdClose = el("button", "wb-sql-card__btn");
  erdClose.type = "button";
  erdClose.textContent = "Закрыть";
  erdBar.appendChild(erdTitle);
  erdBar.appendChild(erdHint);
  erdBar.appendChild(erdSpacer);
  erdBar.appendChild(erdClose);
  const erdScroll = el("div", "wb-sql-erd__scroll");
  erd.appendChild(erdBar);
  erd.appendChild(erdScroll);
  card.appendChild(erd);

  let erdOpen = false;

  function closeErd(): void {
    erdOpen = false;
    erd.hidden = true;
    erdBtn.classList.remove("is-on");
  }

  function openErd(): void {
    erdOpen = true;
    erd.hidden = false;
    erdBtn.classList.add("is-on");
    const cached = schemaCache.get(schemaKey(state));
    if (cached) {
      renderSqlErd(erdScroll, cached, { onPickTable: showTableData });
    } else {
      erdScroll.textContent = "";
      const wait = el("div", "wb-sql-erd__empty");
      wait.textContent = "Читаю схему базы…";
      erdScroll.appendChild(wait);
      loadSchema(true);
    }
  }

  erdBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (erdOpen) closeErd();
    else openErd();
  });
  erdClose.addEventListener("click", (ev) => {
    ev.stopPropagation();
    closeErd();
  });

  applyVerdict(card, verdict, state.status, initial?.verdict?.message ?? "", obj.id);
  updateSolvedUi(card, state);

  /* ── запуск ── */
  const currentQuery = () => getCodeEditor(host)?.getValue() ?? state.query;
  const currentSetup = () => {
    if (state.dataset !== "custom") return setupSqlFor(state);
    const v = getCodeEditor(
      card.querySelector('[data-sql-field="setup"]') as HTMLElement
    )?.getValue();
    return typeof v === "string" ? v : state.setupSql;
  };

  const setBusy = (on: boolean, label: string) => {
    runBtn.disabled = on;
    checkBtn.disabled = on;
    statusEl.textContent = label;
  };

  async function execute(withCheck: boolean) {
    const query = currentQuery();
    if (!query.trim()) {
      statusEl.textContent = "Сначала напишите запрос.";
      return;
    }
    // Недосланный запрос — до запуска, чтобы у всех был один и тот же текст.
    if (canEdit) flushQuery();
    setBusy(true, "Выполняется…");
    const solution = withCheck && task ? task.solution : undefined;
    const res = await runSql(
      { setup: currentSetup(), query, solution },
      (phase) => {
        statusEl.textContent =
          phase === "loading_runtime" ? "Загрузка SQLite…" : "Выполняется запрос…";
      }
    );

    if (res.schema) {
      schemaCache.set(schemaKey(state), res.schema);
      if (aside) renderSchemaPanel(aside, res.schema, showTableData);
      if (erdOpen) renderSqlErd(erdScroll, res.schema, { onPickTable: showTableData });
    }
    renderResult(out, res.actual);

    let status: "idle" | "ok" | "fail" = "idle";
    let message = "";
    let solvedId: string | undefined;
    if (withCheck && task && res.expected) {
      const v = compareResults(res.actual, res.expected, solutionOrdersRows(task.solution));
      status = v.ok ? "ok" : "fail";
      message = v.message;
      if (v.ok && task.id !== "custom") solvedId = task.id;
    }
    applyVerdict(card, verdict, status, message, obj.id);
    if (solvedId && !state.solvedIds.includes(solvedId)) state.solvedIds.push(solvedId);
    updateSolvedUi(card, { ...state, status });

    if (canEdit) {
      act({
        type: "setResult",
        status,
        output: encodeSqlOutput({
          table: res.actual,
          verdict: withCheck ? { ok: status === "ok", message } : undefined,
        }),
        solvedId,
      });
    }
    setBusy(false, res.actual.error ? "Ошибка в запросе" : "Готово");
  }

  runBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    void execute(false);
  });
  checkBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    void execute(true);
  });

  /** Содержимое таблицы: подставляем запрос в редактор и выполняем. */
  function showTableData(name: string): void {
    const editor = getCodeEditor(host);
    const query = `SELECT * FROM "${name.replace(/"/g, '""')}"`;
    if (editor) editor.setValue(query);
    closeErd();
    void execute(false);
  }

  /* Схему подгружаем сразу, чтобы список таблиц был виден до первого запроса. */
  function loadSchema(force = false): void {
    const key = schemaKey(state);
    if (!force && schemaCache.has(key)) return;
    void runSql({ setup: currentSetup(), query: "SELECT 1" }).then((res) => {
      if (!res.schema) return;
      schemaCache.set(key, res.schema);
      if (aside && aside.isConnected) renderSchemaPanel(aside, res.schema, showTableData);
      if (erdOpen && erdScroll.isConnected) {
        renderSqlErd(erdScroll, res.schema, { onPickTable: showTableData });
      }
    });
  }

  if (state.showSchema) loadSchema();

  g.appendChild(card);
  return g;
}
